import React from "react";
import { DynamicNavigation, Footer } from "./components";
import { useSeo } from "./seo";
import { useNavLinkClick } from "./hooks/useNavLinkClick";
import { Link } from "react-router-dom";

type LegalPageLayoutProps = {
  title: string;
  children: React.ReactNode;
};

export default function LegalPageLayout({ title, children }: LegalPageLayoutProps) {
  const normalizedTitle = title.toLowerCase();
  const legalPath = normalizedTitle.includes("privacy")
    ? "/privacy-policy"
    : normalizedTitle.includes("term")
    ? "/terms-of-service"
    : "/cookie-settings";

  useSeo({
    title: `${title} | Hola World Migration`,
    description: `Read ${title} for Hola World Migration services, policies, and user rights information.`,
    path: legalPath,
    // image property removed to force global OG image
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Legal", url: "/privacy-policy" },
      { name: title, url: legalPath },
    ],
  });

  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "about", label: "About", href: "/about" },
    { id: "services", label: "Services", href: "/services" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];

  const onLinkClick = useNavLinkClick(links);

  return (
    <div className="min-h-screen bg-slate-100 overflow-x-hidden">
      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Logo" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
        />
      </header>

      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <section className="mx-auto w-full max-w-5xl">
          <div className="flex flex-wrap items-center justify-start space-x-2 text-sm text-gray-500 font-medium">
            <Link to="/" aria-label="Home" className="inline-flex items-center">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 7.609c.352 0 .69.122.96.343l.111.1 6.25 6.25v.001a1.5 1.5 0 0 1 .445 1.071v7.5a.89.89 0 0 1-.891.891H9.125a.89.89 0 0 1-.89-.89v-7.5l.006-.149a1.5 1.5 0 0 1 .337-.813l.1-.11 6.25-6.25c.285-.285.67-.444 1.072-.444Zm5.984 7.876L16 9.5l-5.984 5.985v6.499h11.968z" fill="#475569" stroke="#475569" strokeWidth=".094"/>
              </svg>
            </Link>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="m14.413 10.663-6.25 6.25a.939.939 0 1 1-1.328-1.328L12.42 10 6.836 4.413a.939.939 0 1 1 1.328-1.328l6.25 6.25a.94.94 0 0 1-.001 1.328" fill="#CBD5E1"/>
            </svg>
            <Link to="/privacy-policy" className="hover:text-slate-700 transition-colors duration-200">Legal</Link>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="m14.413 10.663-6.25 6.25a.939.939 0 1 1-1.328-1.328L12.42 10 6.836 4.413a.939.939 0 1 1 1.328-1.328l6.25 6.25a.94.94 0 0 1-.001 1.328" fill="#CBD5E1"/>
            </svg>
            <span className="text-indigo-500">{title}</span>
          </div>

          <h1 className="mt-2 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900">{title}</h1>
          <div className="mt-10 space-y-10 text-slate-700 leading-8 text-[15px] md:text-[17px]">
            {children}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
